import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../store/authSlice";
import { buildAdminDashboardPath, getDefaultDashboardTabForRole } from "../router/dashboardNavigation";
import { normalizeRole } from "../utils/rbacHelper";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import logoImage from "../assets/logo.png";
import loginImage from "../assets/loginimage.png";

const AUTH_FONT_FAMILY = '"Inter", "Segoe UI", Arial, sans-serif';
const BRAND_GRADIENT = "linear-gradient(90deg, #22C55E 0%, #2563EB 100%)";
const DESKTOP_PANEL_RADIUS = "0 100% 100% 0 / 0 46% 46% 0";
const DESKTOP_CURVE_VERTICAL_BLEED = 120;
// Browser zoom and Windows display scaling can make a normal laptop appear as a
// "short" CSS viewport. Reserve compact typography only for genuinely tiny
// desktop heights; normal 100% zoom should retain the full desktop layout.
const SHORT_DESKTOP_QUERY = "@media (min-width:900px) and (max-height:520px)";
// A number of Windows laptops expose a narrow CSS viewport at 100% browser
// zoom because of display scaling.  A two-column layout at that width leaves
// the form with too little usable space.  Keep the desktop presentation, but
// use a single, centered login panel until there is room for both columns.
// Mobile browsers can report a wide "desktop site" viewport while the
// physical screen remains narrow. Keep one clear, usable form column through
// common laptop widths too; the visual panel returns only on truly wide space.
// Desktop-site mode on Android/iOS can advertise a very wide virtual viewport.
// Pointer capability remains accurate, so a touch device always gets the
// single-column form instead of a compressed desktop split view.
const COMPACT_DESKTOP_QUERY = "@media (max-width:1535px), (pointer: coarse), (hover: none)";
const FEATURE_ITEMS = [
  { icon: ShieldOutlinedIcon, title: "Secure & Reliable" },
  { icon: AccessTimeOutlinedIcon, title: "Real-time Tracking" },
  { icon: QueryStatsOutlinedIcon, title: "Advanced Reports" },
  { icon: FmdGoodOutlinedIcon, title: "Geo Fencing" },
];
const WEB_APP_BLOCKED_ROLES = new Set(["viewer", "driver", "field_emp"]);
const WEB_APP_BLOCKED_MESSAGE = "Please login on app";

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // One form-first layout is deliberately used everywhere. It prevents browser
  // zoom, Windows scaling, and compact laptop widths from changing the login
  // page into an unusable split layout.
  const useFocusedLoginLayout = true;

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const blockedMessage = sessionStorage.getItem("webLoginBlockedMessage");
    if (!blockedMessage) return;

    setError(blockedMessage);
    sessionStorage.removeItem("webLoginBlockedMessage");
  }, []);

  const identifierHint = useMemo(() => {
    if (!identifier) return "User ID / Email / Mobile";
    const looksLikeEmail = identifier.includes("@");
    const looksLikePhone = /^[0-9+\-\s()]{7,}$/.test(identifier);
    if (looksLikeEmail) return "Looks like Email";
    if (looksLikePhone) return "Looks like Mobile";
    return "Looks like User ID";
  }, [identifier]);

  const IdentifierIcon = useMemo(() => {
    if (!identifier) return PersonOutlineOutlinedIcon;
    const looksLikeEmail = identifier.includes("@");
    const looksLikePhone = /^[0-9+\-\s()]{7,}$/.test(identifier);
    if (looksLikeEmail) return AlternateEmailOutlinedIcon;
    if (looksLikePhone) return PhoneIphoneOutlinedIcon;
    return PersonOutlineOutlinedIcon;
  }, [identifier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setIsLoading(true);

    try {
      const res = await axios.post(`${backendUrl}/auth/login`, {
        identifier,
        password,
      });

      const { token, user } = res.data;
      const normalizedRole = normalizeRole(user?.role);

      if (WEB_APP_BLOCKED_ROLES.has(normalizedRole)) {
        sessionStorage.setItem("webLoginBlockedMessage", WEB_APP_BLOCKED_MESSAGE);
        setError(WEB_APP_BLOCKED_MESSAGE);
        setPassword("");
        return;
      }

      dispatch(loginSuccess({ user, token }));

      const role = normalizedRole || user.role;
      navigate(
        buildAdminDashboardPath({
          tab: getDefaultDashboardTabForRole(role),
        }),
        { replace: true }
      );

      setMessage("Login successful!");
    } catch (err) {
      setError(err.response?.data?.msg || "Invalid credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  // The same focused card is used on phone and web. This keeps the actual form
  // stable and readable instead of relying on a second decorative panel.
  if (useFocusedLoginLayout) {
    return (
      <Box
        sx={{
          minHeight: "100dvh",
          bgcolor: "#f6f8fb",
          display: "grid",
          placeItems: "center",
          px: 2,
          py: 3,
          fontFamily: AUTH_FONT_FAMILY,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 420,
            bgcolor: "#fff",
            border: "1px solid #E7ECF4",
            borderRadius: "24px",
            boxShadow: "0 18px 45px rgba(15, 35, 67, 0.10)",
            p: { xs: 3, sm: 4 },
          }}
        >
          <Stack alignItems="center" spacing={1.1} sx={{ mb: 3.2 }}>
            <Box component="img" src={logoImage} alt="Third Eye Telematics" sx={{ width: 72, height: 72, objectFit: "contain" }} />
            <Typography sx={{ color: "#0F2740", fontSize: 24, fontWeight: 800, lineHeight: 1, textAlign: "center" }}>THIRD EYE</Typography>
            <Typography sx={{ color: "#20B374", fontSize: 21, fontWeight: 800, lineHeight: 1, textAlign: "center" }}>TELEMATICS</Typography>
            <Typography sx={{ color: "#69778D", fontSize: 13, fontWeight: 600 }}>Track. Monitor. Protect.</Typography>
          </Stack>

          <Typography sx={{ color: "#0B2239", fontSize: 31, fontWeight: 800, letterSpacing: "-0.05em", lineHeight: 1.06 }}>Welcome <Box component="span" sx={{ backgroundImage: BRAND_GRADIENT, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Back!</Box></Typography>
          <Typography sx={{ color: "#667085", fontSize: 14, mt: 1, mb: 3 }}>Login to access your GPS tracking dashboard</Typography>

          <Stack component="form" onSubmit={handleSubmit} noValidate spacing={2.1}>
            {message && <Alert severity="success">{message}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <Stack spacing={0.8}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>User ID / Email / Mobile</Typography>
              <TextField value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" fullWidth required placeholder="Enter user id / email / mobile" helperText={identifierHint} InputProps={{ startAdornment: <InputAdornment position="start"><IdentifierIcon sx={{ color: "#475467" }} /></InputAdornment> }} sx={{ "& .MuiOutlinedInput-root": { height: 54, borderRadius: "13px", "& fieldset": { borderColor: "#E1E7F0" } }, "& .MuiFormHelperText-root": { mx: 0 } }} />
            </Stack>
            <Stack spacing={0.8}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0F172A" }}>Password</Typography>
              <TextField value={password} onChange={(e) => setPassword(e.target.value)} type={showPassword ? "text" : "password"} autoComplete="current-password" fullWidth required placeholder="Enter your password" InputProps={{ startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: "#475467" }} /></InputAdornment>, endAdornment: <InputAdornment position="end"><IconButton aria-label="toggle password visibility" edge="end" onClick={() => setShowPassword((value) => !value)}>{showPassword ? <VisibilityOffOutlinedIcon sx={{ color: "#475467" }} /> : <VisibilityOutlinedIcon sx={{ color: "#475467" }} />}</IconButton></InputAdornment> }} sx={{ "& .MuiOutlinedInput-root": { height: 54, borderRadius: "13px", "& fieldset": { borderColor: "#E1E7F0" } } }} />
            </Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={0.4}><Checkbox size="small" sx={{ p: 0.25, color: "#98A2B3", "&.Mui-checked": { color: "#22C55E" } }} /><Typography sx={{ fontSize: 13, fontWeight: 600 }}>Remember Me</Typography></Stack>
              <Typography sx={{ color: "#16A34A", fontSize: 13, fontWeight: 700 }}>Forgot Password</Typography>
            </Stack>
            <Button type="submit" variant="contained" disabled={isLoading} startIcon={!isLoading ? <LoginOutlinedIcon /> : null} sx={{ height: 54, borderRadius: "13px", textTransform: "none", fontSize: 17, fontWeight: 800, background: BRAND_GRADIENT, boxShadow: "0 13px 25px rgba(37,99,235,.20)", "&:hover": { background: BRAND_GRADIENT } }}>{isLoading ? <CircularProgress size={21} color="inherit" /> : "Login"}</Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        overflowY: "auto",
        bgcolor: "#f6f8fb",
        fontFamily: AUTH_FONT_FAMILY,
      }}
    >
      <Box
        sx={{
          display: "flex",
          minHeight: "100dvh",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          [COMPACT_DESKTOP_QUERY]: {
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: { xs: "100%", md: "50%" },
            // Do not use a desktop min-width here. On Windows laptops the
            // browser's effective width may be smaller because of OS scaling,
            // and a fixed minimum makes the form column collapse visually.
            minWidth: 0,
            flex: { xs: "0 0 auto", md: "0 1 50%" },
            minHeight: "100dvh",
            bgcolor: "#ffffff",
            display: "flex",
            justifyContent: "center",
            flexShrink: 1,
            overflow: { xs: "visible", md: "hidden", xl: "visible" },
            boxShadow: { md: "28px 0 60px rgba(15, 23, 42, 0.06)" },
            zIndex: 2,
            [SHORT_DESKTOP_QUERY]: {
              width: "50%",
            },
            [COMPACT_DESKTOP_QUERY]: {
              width: "100%",
              flex: "1 1 auto",
              minHeight: "100dvh",
              overflow: "auto",
            },
            "&::before": {
              content: '""',
              display: { xs: "none", xl: "block" },
              position: "absolute",
              top: `-${DESKTOP_CURVE_VERTICAL_BLEED / 2}px`,
              right: { lg: "-152px", xl: "-182px" },
              width: { lg: "248px", xl: "308px" },
              height: `calc(100% + ${DESKTOP_CURVE_VERTICAL_BLEED}px)`,
              background: "transparent",
              borderRight: "6px solid rgba(34, 197, 94, 0.96)",
              borderRadius: DESKTOP_PANEL_RADIUS,
              pointerEvents: "none",
              zIndex: 4,
              [SHORT_DESKTOP_QUERY]: {
                right: "-118px",
                width: "194px",
              },
              [COMPACT_DESKTOP_QUERY]: {
                display: "none",
              },
            },
            "&::after": {
              content: '""',
              display: { xs: "none", xl: "block" },
              position: "absolute",
              top: `-${DESKTOP_CURVE_VERTICAL_BLEED / 2}px`,
              right: { lg: "-146px", xl: "-176px" },
              width: { lg: "236px", xl: "296px" },
              height: `calc(100% + ${DESKTOP_CURVE_VERTICAL_BLEED}px)`,
              background: "#ffffff",
              borderRadius: DESKTOP_PANEL_RADIUS,
              pointerEvents: "none",
              zIndex: 3,
              [SHORT_DESKTOP_QUERY]: {
                right: "-112px",
                width: "182px",
              },
              [COMPACT_DESKTOP_QUERY]: {
                display: "none",
              },
            },
          }}
        >
	          <Box
	            sx={{
	              position: "relative",
	              zIndex: 5,
	              width: "100%",
                minWidth: 0,
	              maxWidth: 500,
                mx: "auto",
                boxSizing: "border-box",
	              px: { xs: 3, sm: 5, md: 5, xl: 6 },
	              py: { xs: 4, sm: 5, md: 4.5, xl: 6 },
	              display: "flex",
	              flexDirection: "column",
	              // Keeping a stable top flow avoids the large gaps that appear
                // when zoom changes the effective desktop breakpoint.
	              justifyContent: "flex-start",
                alignItems: "stretch",
	              gap: { xs: 3, lg: 2.4, xl: 4 },
                [SHORT_DESKTOP_QUERY]: {
                  paddingTop: 22,
                  paddingBottom: 18,
                  paddingLeft: 28,
                  paddingRight: 48,
                  gap: 12,
                },
                [COMPACT_DESKTOP_QUERY]: {
                  maxWidth: 430,
                  paddingTop: 36,
                  paddingBottom: 28,
                  paddingLeft: 28,
                  paddingRight: 28,
                  gap: 24,
                },
	            }}
	          >
	            <Stack
                spacing={{ xs: 4, lg: 3.2, xl: 5 }}
                sx={{
                  width: "100%",
                  minWidth: 0,
                  alignItems: "stretch",
                  [SHORT_DESKTOP_QUERY]: {
                    gap: 12,
                  },
                }}
              >
	              <Stack direction="row" spacing={2.2} alignItems="center" sx={{ minWidth: 0 }}>
                <Box
                  component="img"
                  src={logoImage}
                  alt="Third Eye Telematics"
	                  sx={{
	                    width: { xs: 74, sm: 88, lg: 88, xl: 108 },
	                    height: { xs: 74, sm: 88, lg: 88, xl: 108 },
	                    objectFit: "contain",
	                    flexShrink: 0,
                      [SHORT_DESKTOP_QUERY]: {
                        width: 64,
                        height: 64,
                      },
	                  }}
	                />
                <Stack spacing={0.3} sx={{ minWidth: 0 }}>
                  <Typography
	                    sx={{
	                      fontSize: { xs: 22, sm: 28, lg: 29, xl: 34 },
	                      lineHeight: 1,
	                      fontWeight: 800,
	                      letterSpacing: "-0.04em",
	                      color: "#0F2740",
                        [SHORT_DESKTOP_QUERY]: {
                          fontSize: 22,
                        },
	                    }}
	                  >
                    THIRD EYE
                  </Typography>
                  <Typography
	                    sx={{
	                      fontSize: { xs: 20, sm: 25, lg: 28, xl: 33 },
	                      lineHeight: 1,
	                      fontWeight: 700,
	                      letterSpacing: "-0.03em",
	                      color: "#20B374",
                        [SHORT_DESKTOP_QUERY]: {
                          fontSize: 22,
                        },
	                    }}
	                  >
                    TELEMATICS
                  </Typography>
                  <Typography
	                    sx={{
	                      pt: 1,
	                      color: "#667085",
	                      fontSize: { xs: 14, sm: 15, lg: 14.5, xl: 16 },
	                      fontWeight: 500,
                        [SHORT_DESKTOP_QUERY]: {
                          paddingTop: 6,
                          fontSize: 12,
                        },
	                    }}
	                  >
                    Track. Monitor. Protect.
                  </Typography>
                </Stack>
              </Stack>

              <Box
	                sx={{
	                  position: "relative",
	                  width: "100%",
                    minWidth: 0,
	                  maxWidth: 460,
	                  mt: { xs: 0.8, sm: 1.4, md: 2.2, xl: 6.2 },
                    [SHORT_DESKTOP_QUERY]: {
                      marginTop: 2,
                    },
                    [COMPACT_DESKTOP_QUERY]: {
                      maxWidth: 430,
                      marginTop: 8,
                    },
	                }}
	              >
                <Stack spacing={1.4} sx={{ width: "100%", alignItems: "stretch" }}>
                  <Typography
	                    sx={{
	                      fontSize: { xs: 42, sm: 52, lg: 46, xl: 58 },
                        display: "block",
                        width: "100%",
	                      lineHeight: 1.04,
	                      fontWeight: 800,
	                      letterSpacing: "-0.05em",
	                      color: "#0B2239",
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        [SHORT_DESKTOP_QUERY]: {
                          fontSize: 34,
                          lineHeight: 1.02,
                        },
	                    }}
	                  >
                    Welcome{" "}
                    <Box
                      component="span"
                      sx={{
                        backgroundImage: BRAND_GRADIENT,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      Back!
                    </Box>
                  </Typography>
                  <Typography
	                    sx={{
	                      color: "#667085",
	                      fontSize: { xs: 17, lg: 15.5, xl: 18 },
                        display: "block",
                        width: "100%",
                        lineHeight: 1.55,
	                      fontWeight: 500,
                        whiteSpace: "normal",
                        wordBreak: "normal",
                        overflowWrap: "normal",
                        [SHORT_DESKTOP_QUERY]: {
                          fontSize: 12.5,
                        },
	                    }}
	                  >
                    Login to access your GPS tracking dashboard
                  </Typography>
                </Stack>

	                <Stack
                    spacing={2.3}
                    component="form"
                    onSubmit={handleSubmit}
                    noValidate
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      mt: { xs: 2.4, sm: 2.8, md: 2.2 },
                      [SHORT_DESKTOP_QUERY]: {
                        gap: 8,
                        marginTop: 1.5,
                      },
                    }}
                  >
                {message && <Alert severity="success">{message}</Alert>}
                {error && <Alert severity="error">{error}</Alert>}

                <Stack spacing={1}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                    User ID / Email / Mobile
                  </Typography>
                  <TextField
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    autoComplete="username"
                    fullWidth
                    required
                    placeholder="Enter user id / email / mobile"
                    helperText={identifierHint}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <IdentifierIcon sx={{ color: "#475467", fontSize: 24 }} />
                        </InputAdornment>
                      ),
                    }}
	                    sx={{
	                      "& .MuiOutlinedInput-root": {
	                        height: 58,
                        borderRadius: "14px",
                        bgcolor: "#fff",
                        fontSize: 16,
                        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
                        "& fieldset": { borderColor: "#E5E7EB" },
                        "&:hover fieldset": { borderColor: "#CBD5E1" },
	                        "&.Mui-focused fieldset": { borderColor: "#22C55E", borderWidth: 1 },
                          [SHORT_DESKTOP_QUERY]: {
                            height: 44,
                            fontSize: 14,
                          },
	                        [COMPACT_DESKTOP_QUERY]: {
                          height: 52,
                          fontSize: 15,
                        },
	                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#98A2B3",
                        opacity: 1,
                      },
                      "& .MuiFormHelperText-root": {
                        mx: 0,
                        mt: 0.8,
                      },
                    }}
                  />
                </Stack>

                <Stack spacing={1}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                    Password
                  </Typography>
                  <TextField
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    fullWidth
                    required
                    placeholder="Enter your password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: "#475467", fontSize: 24 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            edge="end"
                            onClick={() => setShowPassword((s) => !s)}
                          >
                            {showPassword ? (
                              <VisibilityOffOutlinedIcon sx={{ color: "#475467" }} />
                            ) : (
                              <VisibilityOutlinedIcon sx={{ color: "#475467" }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
	                    sx={{
	                      "& .MuiOutlinedInput-root": {
	                        height: 58,
                        borderRadius: "14px",
                        bgcolor: "#fff",
                        fontSize: 16,
                        boxShadow: "0 1px 2px rgba(16, 24, 40, 0.03)",
                        "& fieldset": { borderColor: "#E5E7EB" },
                        "&:hover fieldset": { borderColor: "#CBD5E1" },
	                        "&.Mui-focused fieldset": { borderColor: "#22C55E", borderWidth: 1 },
                          [SHORT_DESKTOP_QUERY]: {
                            height: 44,
                            fontSize: 14,
                          },
	                        [COMPACT_DESKTOP_QUERY]: {
                          height: 52,
                          fontSize: 15,
                        },
	                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#98A2B3",
                        opacity: 1,
                      },
                    }}
                  />
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ pt: 0.2 }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Checkbox
                      size="small"
                      sx={{
                        color: "#98A2B3",
                        p: 0.25,
                        "&.Mui-checked": { color: "#22C55E" },
                      }}
                    />
                    <Typography sx={{ fontSize: 15, color: "#101828", fontWeight: 500 }}>
                      Remember Me
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#16A34A",
                    }}
                  >
                    Forgot Password
                  </Typography>
                </Stack>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  startIcon={!isLoading ? <LoginOutlinedIcon sx={{ fontSize: 22 }} /> : null}
	                  sx={{
	                    mt: 0.8,
	                    height: 60,
                    borderRadius: "14px",
                    textTransform: "none",
                    fontFamily: AUTH_FONT_FAMILY,
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#fff",
                    background: BRAND_GRADIENT,
                    boxShadow: "0 18px 30px rgba(37, 99, 235, 0.20)",
                    "&:hover": {
                      background: BRAND_GRADIENT,
                      boxShadow: "0 24px 40px rgba(37, 99, 235, 0.28)",
                    },
	                    "&.Mui-disabled": {
	                      color: "rgba(255,255,255,0.78)",
	                      background: BRAND_GRADIENT,
	                    },
                        [SHORT_DESKTOP_QUERY]: {
                          marginTop: 2,
                          height: 48,
                          fontSize: 15,
                        },
	                      [COMPACT_DESKTOP_QUERY]: {
                        height: 52,
                        fontSize: 16,
                      },
	                  }}
	                >
                  {isLoading ? (
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <CircularProgress size={18} color="inherit" />
                      <span>Logging in...</span>
                    </Stack>
                  ) : (
                    "Login"
                  )}
                </Button>

                </Stack>
              </Box>
            </Stack>

	            <Stack
                spacing={2.5}
                sx={{
                  width: "100%",
                  minWidth: 0,
                  [SHORT_DESKTOP_QUERY]: {
                    gap: 4,
                  },
                }}
              >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(2, minmax(0, 1fr))",
                    xl: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: { xs: 1.5, lg: 0 },
	                  pt: { xs: 0.5, lg: 0.6, xl: 1.5 },
                    [SHORT_DESKTOP_QUERY]: {
                      paddingTop: 0,
                    },
	                }}
	              >
                {FEATURE_ITEMS.map(({ icon: Icon, title }, index) => (
                  <Stack
                    key={title}
                    alignItems="center"
                    spacing={1.3}
                    sx={{
                      textAlign: "center",
                      px: { xs: 1, lg: 1.4 },
                      py: 0.5,
                      borderRight: {
                        xl: index === FEATURE_ITEMS.length - 1 ? "none" : "1px solid #E4E7EC",
                      },
                    }}
                  >
                    <Box
	                      sx={{
	                        width: { xs: 48, lg: 42, xl: 48 },
	                        height: { xs: 48, lg: 42, xl: 48 },
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
	                        color: "#20B374",
	                        bgcolor: alpha("#22C55E", 0.08),
                            [SHORT_DESKTOP_QUERY]: {
                              width: 30,
                              height: 30,
                            },
	                      }}
	                    >
	                      <Icon
                          sx={{
                            fontSize: 26,
                            [SHORT_DESKTOP_QUERY]: {
                              fontSize: 17,
                            },
                          }}
                        />
	                    </Box>
	                    <Typography
	                      sx={{
	                        color: "#101828",
	                        fontSize: { xs: 15, lg: 14, xl: 16 },
	                        fontWeight: 600,
	                        lineHeight: 1.35,
                          [SHORT_DESKTOP_QUERY]: {
                            fontSize: 11.5,
                          },
	                      }}
	                    >
                      {title}
                    </Typography>
                  </Stack>
                ))}
              </Box>

              <Typography
	                sx={{
	                  color: "#667085",
	                  fontSize: 14,
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", lg: "flex-start" },
	                  gap: 1,
	                  pt: { xs: 0, lg: 0.2, xl: 1 },
                    [SHORT_DESKTOP_QUERY]: {
                      fontSize: 11.5,
                      paddingTop: 0,
                    },
                }}
              >
                <ShieldOutlinedIcon sx={{ color: "#16A34A", fontSize: 18 }} />
                © 2026 Third Eye Telematics. All rights reserved.
              </Typography>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flex: 1,
            minWidth: 0,
            minHeight: "100dvh",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#f8fafc",
            overflow: "hidden",
            position: "relative",
            zIndex: 1,
            [SHORT_DESKTOP_QUERY]: {
              flexBasis: 0,
            },
            [COMPACT_DESKTOP_QUERY]: {
              display: "none",
            },
          }}
        >
          <Box
            component="img"
            src={loginImage}
            alt="GPS tracking visual"
            sx={{
              width: "100%",
              height: "100%",
              maxWidth: "100%",
              maxHeight: "100vh",
              objectFit: "contain",
              objectPosition: "center center",
              userSelect: "none",
              pointerEvents: "none",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
